import { Formik, Form as FormikForm } from 'formik';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import {
  useRef,
  useCallback,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { StaticGoogleMap } from '@hivebrite/design-system/StaticGoogleMap';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { getInitialValues } from 'hb-react/frontoffice/components/CheckInFormModal/utils/getInitialValues';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting/UserTargeting';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { Location } from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import MapPreview from 'hb-react/shared/components/MapPreview';
import QuillEditorWrappedV3 from 'hb-react/shared/components/QuillEditorWrappedV3';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

const Form = forwardRef(
  (
    {
      canDisplayEmojiPicker,
      checkIn,
      isLoading,
      isUpdatingCheckIn,
      modalElement,
      modalHeaderId,
      onCloseModal,
      onCreate,
      onUpdate,
      onToggleEmojiPicker,
    },
    ref,
  ) => {
    const { flagGoogleMapsComponent } = useFeatureFlag();

    const [isSaving, setIsSaving] = useState(false);
    const [notifyPeoples, setNotifyPeoples] = useState([]);

    const { currentUser } = useDefaultAppContexts();
    const isMobileDevice = useIsMobileDevice();

    const formikRef = useRef();
    const userTargetingRef = useRef();

    const initialValues = getInitialValues({ checkIn });

    // User can't notify on edit
    const userCanNotify = useMemo(
      () =>
        isUpdatingCheckIn
          ? false
          : currentUser.notify_rights.user_can_notify_on_current_location,
      [currentUser, isUpdatingCheckIn],
    );
    const quillEditorInputName = 'location[content_html]';

    // Share function(s) in ref.
    useImperativeHandle(ref, () => ({
      canDisplayDiscardChangesModal: () =>
        !isEqual(formikRef.current.initialValues, formikRef.current.values),
    }));

    const handleOpenNotifyModal = useCallback(() => {
      // Select current notifyPeoples in UserTargeting modal if the user re-opens it
      // Only working on desktop by current UserTargeting app design
      if (userTargetingRef.current && !isMobileDevice) {
        userTargetingRef.current.selectUsers(notifyPeoples);
      }
    }, [notifyPeoples, userTargetingRef.current]);
    const handleValidateNotifySelection = useCallback(
      (selectedUsers, selectedUsersIds) => {
        if (!isMobileDevice) {
          setNotifyPeoples(selectedUsers);
        }
        if (formikRef.current) {
          formikRef.current.setFieldValue(
            'targeted_user_ids',
            selectedUsersIds,
          );
        }
      },
      [formikRef.current],
    );

    const handleSubmit = useCallback(
      async (values) => {
        setIsSaving(true);

        try {
          let promise;

          if (isUpdatingCheckIn) {
            promise = axios.put(
              Routes.user_location_path(currentUser.id, checkIn.id),
              values,
            );
          } else {
            promise = axios.post(
              Routes.user_locations_path(currentUser.id),
              values,
            );
          }

          await promise
            .then(({ data }) => {
              if (isUpdatingCheckIn) {
                displayNotif('success', __('Successfully updated'));
                if (onUpdate) {
                  onUpdate(data.location);
                }
              } else {
                displayNotif('success', __('Successfully created'));
                if (onCreate) {
                  onCreate(data.location);
                }
              }

              onCloseModal();
            })
            .catch(() => {
              setIsSaving(false);
            });
        } catch (_error) {
          setIsSaving(false);
        }
      },
      [currentUser, checkIn, isUpdatingCheckIn, onUpdate, onCreate],
    );

    if (isLoading) {
      return null;
    }

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          formikRef.current = formik;

          return (
            <FormikForm style={{ width: '100%' }}>
              <RequiredExplanation mt={0} mb={5} />

              <Box
                display={{ _: 'block', m: 'flex' }}
                mb={{ _: 6, m: 8 }}
                alignItems={{ _: 'inherit', m: 'flex-start' }}
              >
                <Box alignItems="center" mb={{ _: 6, m: 0 }}>
                  <AvatarWrapper>
                    <Image src={currentUser.photo_thumb_url} alt="" />
                  </AvatarWrapper>
                  <Box
                    flex={1}
                    font="h3-semibold"
                    display={{ _: 'flex', m: 'none' }}
                  >
                    {currentUser.name}
                  </Box>
                </Box>

                <Box
                  flex={1}
                  flexDirection="column"
                  width={{ _: '100%', m: '1%' }}
                >
                  <QuillEditorWrappedV3
                    autoFocus
                    ariaLabelledBy={modalHeaderId}
                    containerElement={modalElement}
                    minHeight={100}
                    name={quillEditorInputName}
                    onToggleEmojiPicker={onToggleEmojiPicker}
                    options={{
                      embedLy: false,
                      toolbar: {
                        images: false,
                        attachments: false,
                        links: true,
                        emojiPicker: canDisplayEmojiPicker,
                        mentions: mentionsOptions,
                      },
                      toolbarFloats: [
                        {
                          key: 'link',
                          items: [
                            {
                              type: 'link',
                            },
                          ],
                        },
                      ],
                    }}
                    placeholder={__('Add a message about your check-in')}
                  />
                </Box>
              </Box>

              <Location
                name="location"
                label={__('Check-in location')}
                validate={{
                  required: true,
                }}
              />

              {flagGoogleMapsComponent ? (
                <StaticGoogleMap
                  apiKey={window.__HB_ENV__.GOOGLEMAPS_API_KEY}
                  enableBorder
                  enablePreview
                  errorMessage={__('StaticGoogleMap|Error loading map')}
                  latitude={formik?.values?.location?.lat}
                  longitude={formik?.values?.location?.lng}
                  previewMessage={__(
                    'Share your location to preview it on the map',
                  )}
                  zoom={13}
                />
              ) : (
                <MapPreview
                  lat={formik?.values?.location?.lat}
                  lng={formik?.values?.location?.lng}
                  width="100%"
                  height={200}
                  zoom={14}
                />
              )}

              <Box justifyContent="flex-end" mt={7}>
                {userCanNotify && (
                  <UserTargeting
                    ref={userTargetingRef}
                    openByClickOn={
                      <Button
                        aria-hidden="true"
                        as="span"
                        dataTestId="user-targeting-button"
                        // Don't put double loader, just put primary button on loading state
                        isDisabled={isSaving}
                        icon={() => (
                          <FontAwesomeIcon
                            iconName="bell"
                            height="16px"
                            iconType={CATEGORIES.SOLID}
                          />
                        )}
                        variant="secondary"
                      >
                        {__('Notify people')}
                        {formik.values?.targeted_user_ids?.length > 0 &&
                          ` · ${formik.values?.targeted_user_ids.length}`}
                      </Button>
                    }
                    defaultQuery={{
                      targeting_type: checkIn?.type,
                    }}
                    maxTargetedUsers={
                      checkIn?.user_targeting?.max_targeted_users
                    }
                    onOpen={handleOpenNotifyModal}
                    onValidateSelection={handleValidateNotifySelection}
                  />
                )}
                <Button type="submit" isLoading={isSaving} ml={3}>
                  {isUpdatingCheckIn ? __('Save changes') : __('Publish')}
                </Button>
              </Box>

              <ErrorFocus />
            </FormikForm>
          );
        }}
      </Formik>
    );
  },
);

Form.propTypes = {
  canDisplayEmojiPicker: PropTypes.bool,
  checkIn: PropTypes.object,
  isLoading: PropTypes.bool,
  isUpdatingCheckIn: PropTypes.bool,
  modalElement: PropTypes.object,
  modalHeaderId: PropTypes.string,
  onCloseModal: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  onToggleEmojiPicker: PropTypes.func,
};

export default Form;

const AvatarWrapper = styled(Box).attrs(() => ({
  height: { _: 48, m: 60 },
  minWidth: { _: 48, m: 60 },
  mr: 3,
  width: { _: 48, m: 60 },
}))`
  user-select: none;

  img {
    border-radius: 50%;
    width: 100%;
  }
`;
